import { useEffect, useRef, useState } from 'react';
import { VIRAL_ECHO_HEAL_FX_TOTAL_MS } from '../config/viralEchoHealEffect';

const processedKeysByGame = new Map();
const hydratedGames = new Set();

const GENERAL_MARK_STRATEGEM_IDS = new Set([56, 185, 188, 220]);

function isGeneralMarkStrategem(s) {
  if (!s || s.instanceId == null) return false;
  if (!GENERAL_MARK_STRATEGEM_IDS.has(Number(s.id))) return false;
  const t = (s.type && String(s.type)) || '';
  if (!t) return true;
  return /strategem/i.test(t);
}

/** General portrait data-zone for the player who cast (healed side for 185/188/220/56). */
function generalDataZoneForCaster(casterId, viewerUserId) {
  return String(casterId) === String(viewerUserId) ? 'player_general' : 'opponent_general';
}

/** Opposing general (damage side for 185/188/220). */
function enemyGeneralDataZoneForCaster(casterId, viewerUserId) {
  return String(casterId) === String(viewerUserId) ? 'opponent_general' : 'player_general';
}

/**
 * Strategem 56 (Viral Echo): green + on healed general.
 * Strategems 185 / 188 / 220: green + on caster general and red × on enemy general (same layout/timing as Viral Echo).
 */
export function useViralEchoHealFx(gameState, viewerUserId) {
  const [active, setActive] = useState([]);
  const gameIdRef = useRef(null);

  useEffect(() => {
    if (!gameState?.state || viewerUserId == null) return;

    const gid = gameState.gameId != null ? String(gameState.gameId) : '_';

    if (gameIdRef.current !== gid) {
      gameIdRef.current = gid;
      setActive([]);
    }

    if (!processedKeysByGame.has(gid)) {
      processedKeysByGame.set(gid, new Set());
    }
    const processed = processedKeysByGame.get(gid);

    if (!hydratedGames.has(gid)) {
      hydratedGames.add(gid);
      Object.keys(gameState.state).forEach((casterId) => {
        gameState.state[casterId]?.completedOrders?.forEach((st) => {
          if (isGeneralMarkStrategem(st)) {
            processed.add(`gm-${st.id}-${casterId}-${st.instanceId}`);
          }
        });
      });
      return;
    }

    Object.keys(gameState.state).forEach((casterId) => {
      const player = gameState.state[casterId];
      if (!player?.completedOrders) return;

      player.completedOrders.forEach((strategem) => {
        if (!isGeneralMarkStrategem(strategem)) return;

        const sid = Number(strategem.id);
        const dedupeKey = `gm-${sid}-${casterId}-${strategem.instanceId}`;
        if (processed.has(dedupeKey)) return;
        processed.add(dedupeKey);

        const t = Date.now();

        if (sid === 56) {
          const generalDataZone = generalDataZoneForCaster(casterId, viewerUserId);
          const animKey = `gm56-${strategem.instanceId}-${t}`;
          setActive((prev) => [...prev, { key: animKey, generalDataZone, animationKey: animKey, variant: 'heal' }]);
          window.setTimeout(() => {
            setActive((prev) => prev.filter((p) => p.key !== animKey));
          }, VIRAL_ECHO_HEAL_FX_TOTAL_MS + 80);
          return;
        }

        if (sid === 185 || sid === 188 || sid === 220) {
          const healZone = generalDataZoneForCaster(casterId, viewerUserId);
          const dmgZone = enemyGeneralDataZoneForCaster(casterId, viewerUserId);
          const healKey = `gm${sid}-h-${strategem.instanceId}-${t}`;
          const dmgKey = `gm${sid}-d-${strategem.instanceId}-${t}`;
          setActive((prev) => [
            ...prev,
            { key: healKey, generalDataZone: healZone, animationKey: healKey, variant: 'heal' },
            { key: dmgKey, generalDataZone: dmgZone, animationKey: dmgKey, variant: 'damage' },
          ]);
          window.setTimeout(() => {
            setActive((prev) => prev.filter((p) => p.key !== healKey && p.key !== dmgKey));
          }, VIRAL_ECHO_HEAL_FX_TOTAL_MS + 80);
        }
      });
    });

    const setForGame = processedKeysByGame.get(gid);
    if (setForGame && setForGame.size > 120) {
      const keys = Array.from(setForGame);
      keys.slice(0, keys.length - 80).forEach((k) => setForGame.delete(k));
    }
  }, [gameState, gameState?.stateVersion, viewerUserId]);

  return { activeViralEchoHealFx: active };
}

export default useViralEchoHealFx;
